// src/components/resources/tools/generators/ColorPalette/palette-analysis.ts
//
// Pure palette-analysis logic harvested verbatim from ColorPaletteGenerator.tsx
// (A11/C1). It scores a generated palette for harmony, accessibility,
// temperature balance, diversity, and contrast, and derives recommendations /
// warnings. Framework-free and unit-tested.
import type { ColorAnalysis, ColorInfo } from './types'

/**
 * Analyze a generated palette.
 *
 * @param colors - The generated colors (empty yields a "FAIL" baseline)
 * @returns A {@link ColorAnalysis} of harmony, accessibility, temperature,
 *   diversity, and contrast plus recommendations/warnings.
 */
export function analyzeColorInfoPalette(colors: ColorInfo[]): ColorAnalysis {
  if (colors.length === 0) {
    return {
      harmonyScore: 0,
      accessibilityLevel: 'FAIL',
      temperatureBalance: 'balanced',
      recommendations: ['Add colors to analyze'],
      warnings: ['No colors provided'],
      diversityScore: 0,
      contrastScore: 0,
    }
  }
  // Harmony: based on unique hues and evenness
  const uniqueHues = new Set(colors.map(c => Math.round(c.hsl[0] / 15) * 15)).size
  const harmonyScore = Math.min(100, Math.round((uniqueHues / colors.length) * 100 + 10))
  // Accessibility
  const accessibleColors = colors.filter(c => c.wcag === 'aa' || c.wcag === 'aaa')
  const accessibilityLevel = accessibleColors.length === colors.length ? 'AAA' :
                            accessibleColors.length > colors.length / 2 ? 'AA' : 'FAIL'
  // Temperature
  const warmColors = colors.filter(c => c.temperature === 'warm').length
  const coolColors = colors.filter(c => c.temperature === 'cool').length
  const neutralColors = colors.filter(c => c.temperature === 'neutral').length

  let temperatureBalance: 'warm' | 'cool' | 'balanced' = 'balanced'
  if (warmColors > coolColors + neutralColors) temperatureBalance = 'warm'
  else if (coolColors > warmColors + neutralColors) temperatureBalance = 'cool'
  // Diversity
  const diversityScore = Math.min(100, (uniqueHues / colors.length) * 100)
  // Contrast
  const avgContrast = colors.reduce((sum, c) => sum + c.contrast, 0) / colors.length
  const contrastScore = Math.min(100, (avgContrast / 21) * 100)
  // Recommendations & warnings
  const recommendations: string[] = []
  const warnings: string[] = []

  if (accessibilityLevel === 'FAIL') {
    warnings.push('Some colors may not meet accessibility standards')
    recommendations.push('Consider using higher contrast colors')
  }

  if (diversityScore < 50) {
    recommendations.push('Add more color variety to improve diversity')
  }

  if (contrastScore < 60) {
    recommendations.push('Increase contrast between colors for better visibility')
  }

  if (temperatureBalance === 'warm' || temperatureBalance === 'cool') {
    recommendations.push(`Consider adding more ${temperatureBalance === 'warm' ? 'cool' : 'warm'} colors for balance`)
  }

  return {
    harmonyScore,
    accessibilityLevel,
    temperatureBalance,
    recommendations,
    warnings,
    diversityScore,
    contrastScore,
  }
}
